/**
 * Search + filters above the Creators dashboard list: one search box (creator,
 * agency, channel, slug or a pasted link), All · Creators · Agencies, and a
 * status filter. The caller owns the values (they live in the URL).
 */

import React from 'react';
import { SearchIcon, Tabs } from '../ui';
import { DashboardStatusFilter, DashboardTypeFilter } from './creatorDashboardSearch';

const STATUS_LABELS: Record<DashboardStatusFilter, string> = {
  any: 'Any status',
  active: 'Active',
  deactivated: 'Deactivated',
  locked: 'Locked out',
};

interface ToolbarProps {
  query: string;
  onQueryChange: (q: string) => void;
  type: DashboardTypeFilter;
  onTypeChange: (t: DashboardTypeFilter) => void;
  status: DashboardStatusFilter;
  onStatusChange: (s: DashboardStatusFilter) => void;
  counts: Record<DashboardTypeFilter, number>;
  shown: number;
  total: number;
  refreshing: boolean;
  onClearAll: () => void;
}

const CreatorDashboardsToolbar: React.FC<ToolbarProps> = ({
  query,
  onQueryChange,
  type,
  onTypeChange,
  status,
  onStatusChange,
  counts,
  shown,
  total,
  refreshing,
  onClearAll,
}) => {
  const filtered = query.trim() !== '' || type !== 'all' || status !== 'any';
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
      <div style={{ position: 'relative', flex: '1 1 300px', minWidth: 220, maxWidth: 460 }}>
        <span
          style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'var(--fg-4)', display: 'inline-flex' }}
        >
          <SearchIcon />
        </span>
        <input
          type="text"
          role="searchbox"
          aria-label="Search creator dashboards"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && query) {
              e.preventDefault();
              onQueryChange('');
            }
          }}
          placeholder="Search creators, agencies, channels, slugs or links…"
          style={{
            width: '100%',
            boxSizing: 'border-box',
            height: 36,
            padding: '0 34px 0 32px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-1)',
            background: 'var(--bg-surface-1)',
            fontSize: 13.5,
            fontFamily: 'var(--font-sans)',
            color: 'var(--fg-1)',
          }}
        />
        {query && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onQueryChange('')}
            style={{
              position: 'absolute',
              right: 4,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 28,
              height: 28,
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              background: 'transparent',
              color: 'var(--fg-3)',
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            ✕
          </button>
        )}
      </div>

      <Tabs
        value={type}
        onChange={onTypeChange}
        size="sm"
        ariaLabel="Dashboard type"
        options={[
          { value: 'all', label: 'All', count: counts.all },
          { value: 'creator', label: 'Creators', count: counts.creator },
          { value: 'agency', label: 'Agencies', count: counts.agency },
        ]}
      />

      <select
        aria-label="Filter by status"
        value={status}
        onChange={(e) => onStatusChange(e.target.value as DashboardStatusFilter)}
        style={{
          height: 32,
          padding: '0 10px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-1)',
          background: 'var(--bg-surface-1)',
          fontSize: 12.5,
          fontFamily: 'var(--font-sans)',
          color: 'var(--fg-1)',
          cursor: 'pointer',
        }}
      >
        {(Object.keys(STATUS_LABELS) as DashboardStatusFilter[]).map((s) => (
          <option key={s} value={s}>
            {STATUS_LABELS[s]}
          </option>
        ))}
      </select>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: 'var(--fg-4)' }}>
        {refreshing && <span aria-live="polite">Refreshing…</span>}
        <span aria-live="polite">{filtered ? `Showing ${shown} of ${total}` : `${total} dashboard${total === 1 ? '' : 's'}`}</span>
        {filtered && (
          <button
            type="button"
            onClick={onClearAll}
            style={{ border: 'none', background: 'none', padding: 0, color: 'var(--accent)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
};

export default CreatorDashboardsToolbar;
